import { useState, type FunctionComponent } from "react";
import { UsergroupAddOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, Space, Table, Tag, Typography } from "antd";
import { useNavigate } from "react-router-dom";

import {
  ChallengeParticipantSource,
  ChallengeParticipantStatus,
  type ChallengeParticipantEntity,
} from "@fitness-app/data-models/entities/Challenge";
import { type Trainee } from "@fitness-app/data-models/entities/Trainee";

import TraineeSimulatorButton from "~/modules/Trainee/TraineeSimulator/TraineeSimulatorButton";
import { supabase } from "~/store/initializeStore";
import ChallengeCohortAssignModal from "./ChallengeCohortAssignModal";
import { formatChallengeDate } from "./helpers";

type ParticipantRow = ChallengeParticipantEntity & {
  trainee: Pick<Trainee, "id" | "firstName" | "lastName" | "email"> | null;
};

type Props = {
  challengeId: string;
};

const sourceLabels: Record<string, string> = {
  [ChallengeParticipantSource.Assigned]: "Przypisany",
  [ChallengeParticipantSource.SelfJoined]: "Dołączył sam",
};

const statusLabels: Record<string, string> = {
  [ChallengeParticipantStatus.Active]: "Aktywny",
  [ChallengeParticipantStatus.Pending]: "Oczekuje",
  [ChallengeParticipantStatus.Left]: "Opuścił",
  [ChallengeParticipantStatus.Completed]: "Zaliczone",
};

const statusColors: Record<string, string> = {
  [ChallengeParticipantStatus.Active]: "green",
  [ChallengeParticipantStatus.Pending]: "gold",
  [ChallengeParticipantStatus.Left]: "red",
  [ChallengeParticipantStatus.Completed]: "blue",
};

const ChallengeParticipantsTable: FunctionComponent<Props> = ({ challengeId }) => {
  const navigate = useNavigate();
  const [isAssignOpen, setIsAssignOpen] = useState(false);

  const participantsQuery = useQuery({
    queryKey: ["challenge-participants", challengeId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("challenge_participant")
        .select("*, trainee:traineeId!inner(id, firstName, lastName, email)")
        .eq("challengeId", challengeId)
        .order("joinedAt", { ascending: false })
        .returns<ParticipantRow[]>();

      if (error) {
        throw error;
      }

      return data ?? [];
    },
  });

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button type="primary" icon={<UsergroupAddOutlined />} onClick={() => setIsAssignOpen(true)}>
          Przypisz klientki
        </Button>
      </div>

      <Table<ParticipantRow>
        dataSource={participantsQuery.data ?? []}
        rowKey="id"
        size="middle"
        scroll={{ x: true }}
        loading={participantsQuery.isFetching}
        locale={{ emptyText: "Brak uczestników" }}
        pagination={false}
      >
        <Table.Column<ParticipantRow>
          title="Klientka"
          dataIndex="trainee"
          key="trainee"
          render={(_, row) => (
            <Space direction="vertical" size={0}>
              <Button
                type="link"
                className="px-0 h-auto text-left font-semibold"
                onClick={() => navigate(`/trainee/details/${row.traineeId}`)}
              >
                {row.trainee?.firstName} {row.trainee?.lastName}
              </Button>
              <Typography.Text type="secondary">{row.trainee?.email}</Typography.Text>
            </Space>
          )}
        />
        <Table.Column<ParticipantRow>
          title="Status"
          dataIndex="status"
          key="status"
          render={(status: string) => (
            <Tag color={statusColors[status] ?? "default"}>{statusLabels[status] ?? status}</Tag>
          )}
        />
        <Table.Column<ParticipantRow>
          title="Źródło"
          dataIndex="source"
          key="source"
          render={(source: string) => sourceLabels[source] ?? source}
        />
        <Table.Column<ParticipantRow>
          title="Dołączył"
          dataIndex="joinedAt"
          key="joinedAt"
          render={(joinedAt: string) => formatChallengeDate(joinedAt)}
        />
        <Table.Column<ParticipantRow>
          title="Zaliczono"
          dataIndex="completedAt"
          key="completedAt"
          render={(completedAt: string | null) => (completedAt ? formatChallengeDate(completedAt) : "—")}
        />
        <Table.Column<ParticipantRow>
          title=""
          key="simulator"
          render={(_, row) => <TraineeSimulatorButton traineeId={row.traineeId} />}
        />
      </Table>

      <ChallengeCohortAssignModal
        open={isAssignOpen}
        challengeId={challengeId}
        onClose={() => setIsAssignOpen(false)}
      />
    </Space>
  );
};

export default ChallengeParticipantsTable;
